import { useEffect, useMemo, useState } from 'react';

import { AutocompleteInputChangeReason, Typography } from '@mui/material';
import { Stack, SxProps } from '@mui/system';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import AsyncAutocomplete from '~components/inputFields/autocompletes/asyncAutocomplete';
import ResourceGroupService from '~features/resourceGroups/services/resourceGroupService';
import { BaseReference } from '~interfaces';
import ResourceGroup from '~interfaces/resourceGroup';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

interface ResourceGroupAutocompleteProps {
	onValueChanged: (newValue?: ResourceGroup) => void;
	disableClearable?: boolean;
	value?: ResourceGroup | string;
	filterOrganisationId?: string;
	isInternal?: boolean;
	disabled?: boolean;
	required?: boolean;
	label?: string;
	sx?: SxProps;
}

const service = getService(ResourceGroupService);

const ResourceGroupAutocomplete = ({
	value,
	label,
	disableClearable = false,
	isInternal = false,
	disabled = false,
	required = false,
	filterOrganisationId,
	...props
}: ResourceGroupAutocompleteProps) => {
	const { t } = useTranslation();
	const cacheKey = ['resourceGroups', filterOrganisationId, isInternal];
	const { data } = useSWR(
		cacheKey,
		() =>
			isInternal ?
				service.getResourceGroupsInternal({
					page: '1',
					pageSize: '500',
					organisationId: filterOrganisationId,
				})
			:	service.getResourceGroups({
					page: '1',
					pageSize: '500',
					overwrittenClientId: filterOrganisationId,
				}),
		{
			// Not really the best way, but I'm assuming clients won't have over 500 resource groups for now.
			// Mostly because of time constraint and now wanting to figure out the paging in the auto complete
			// I took a little shortcut. Will revisit this at a later moment.
			keepPreviousData: false,
			revalidateOnMount: true,
		}
	);

	// Hooks and states
	const [filteredResourceGroups, setFilteredResourceGroups] = useState([] as ResourceGroup[]);
	const resourceGroups = useMemo(() => data?.results ?? [], [data?.results]);

	useEffect(() => {
		// This is an okay use-case when data is fetched from API and depends on user input.
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setFilteredResourceGroups(resourceGroups);
	}, [resourceGroups]);

	const selectedValue = useMemo(() => {
		const selectedId = (value as ResourceGroup)?.id ?? value;
		if (selectedId == null || selectedId === '') {
			return null;
		}

		return resourceGroups.find((resourceGroup) => resourceGroup.id === selectedId) ?? null;
	}, [resourceGroups, value]);

	// Input handling
	const handleInputChange = (
		_event: React.SyntheticEvent,
		value: string,
		_reason: AutocompleteInputChangeReason
	) => {
		if (value.length <= 0) {
			setFilteredResourceGroups(resourceGroups);
		} else {
			setFilteredResourceGroups(
				resourceGroups.filter((resourceGroup) =>
					resourceGroup.name.toLowerCase().includes(value.toLowerCase())
				)
			);
		}
	};

	const handleOptionSelected = (value: BaseReference | undefined) => {
		props.onValueChanged(resourceGroups.find((resourceGroup) => resourceGroup.id == value?.id));
	};

	return (
		<Stack sx={{ gap: Dimens.spacingXs }}>
			{label && <Typography variant='labelSm'>{label}</Typography>}
			<AsyncAutocomplete
				placeholder={t('resourceGroup.labels.single', { ns: 'entities' })}
				noOptionsText={t('noResults', { ns: 'system' })}
				options={filteredResourceGroups}
				value={selectedValue}
				onInputChange={handleInputChange}
				onOptionSelected={handleOptionSelected}
				debounce={false}
				required={required}
				disabled={disabled}
				enableInfiniteScroll={false}
				disableClearable={disableClearable}
				sx={{
					width: 200,
					...props.sx,
				}}
			/>
		</Stack>
	);
};

export default ResourceGroupAutocomplete;
